import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  EventTypes,
  GatewayStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  NotificationEvent,
  ViEvent,
} from '@store';

import {
  DeviceErrorMessageTypes,
  DeviceIds,
  DeviceNameCommands,
  DeviceNameProperties,
  ErrorEvent,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  RoomCommands,
  RoomProperties,
} from '@vi-iot/client';
import { Observable, of, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { DatesService } from '../../../../../../shared/services/dates.service';
import { EventDescriptionService } from '../../../../../services/event-description.service';
import { EventsService } from '../../../../../services/events.service';

@Component({
  selector: 'app-event-item',
  templateUrl: './event-item.component.html',
  styleUrls: ['./event-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventItemComponent implements OnInit, OnDestroy {
  @Input() event: ViEvent;
  eventTypes = EventTypes;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly featureNames = FeatureNames;

  boilerSerial: string;

  createdTimestamp: string;
  isDeviceError: boolean;
  notificationEvent: NotificationEvent;
  private timeZone;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public eventsService: EventsService,
    public gatewayStore: GatewayStore,
    public eventDescriptionService: EventDescriptionService,
    public installationDetailsStore: InstallationDetailsStore,
    private translateService: TranslateService,
    private datesService: DatesService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.subscribeToTimezone();
    this.isDeviceError = !!(this.event && this.event.body && this.event.body.errorCode);
    this.notificationEvent = this.event as NotificationEvent;
  }

  getEventTitle(): string {
    const isEventActive = this.event.body.active;

    const errorCodeSplit = this.event.body.errorCode.split('.');
    switch (errorCodeSplit[0]) {
      case DeviceErrorMessageTypes.maintenance:
        return isEventActive
          ? '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.EVENT_TYPE_ACTIVE_MAINTENANCE_NOTIFICATION'
          : '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.EVENT_TYPE_PAST_MAINTENANCE_NOTIFICATION';
      case DeviceErrorMessageTypes.warning:
        return isEventActive
          ? '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.EVENT_TYPE_ACTIVE_WARNING'
          : '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.EVENT_TYPE_PAST_WARNING';
      case DeviceErrorMessageTypes.information:
        return isEventActive
          ? '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.EVENT_TYPE_ACTIVE_INFORMATION_NOTIFICATION'
          : '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.EVENT_TYPE_PAST_INFORMATION_NOTIFICATION';
      case DeviceErrorMessageTypes.statusChanged:
        return isEventActive
          ? '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.EVENT_TYPE_STATUS_ACTIVE'
          : '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.EVENT_TYPE_STATUS_ENDED';
      case DeviceErrorMessageTypes.error:
      default:
        return isEventActive
          ? '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.EVENT_TYPE_ACTIVE_FAULT'
          : '+INSTALLATIONS.INSTALLATION.EVENT_LOG.EVENT_ITEM.EVENT_TYPE_FAULT_ENDED';
    }
  }

  getNotificationMessage(): string {
    return this.eventsService.getNotificationMessage(this.event as NotificationEvent);
  }

  getEventDescription(): string {
    return this.isDeviceError
      ? this.getErrorCodeTranslation(true)
      : this.eventDescriptionService.getDescriptionByEventType(
          this.event as NotificationEvent,
          this.timeZone
        );
  }

  getErrorCodeTranslation(isDescription = false): string {
    let mappedErrorCode = this.event.body.errorCode.replace('.', '_');
    let translationKey = `TI_${mappedErrorCode}`;

    if (isDescription) {
      translationKey = translationKey + '_Desc';
    }

    const translation = this.translateService.instant(translationKey);

    const translationFound = translation !== translationKey;
    if (translationFound) {
      return isDescription ? translation : this.event.body.errorCode + ' - ' + translation;
    } else {
      return isDescription
        ? (this.event as ErrorEvent).body.errorDescription
        : (this.event as ErrorEvent).body.errorCode;
    }
  }

  private subscribeToTimezone(): void {
    this.installationDetailsStore.installationTimezone$
      .pipe(takeUntil(this.ngUnsubscribe$), first())
      .subscribe((timeZone) => {
        this.timeZone = timeZone;
        this.createdTimestamp = this.datesService.getMomentFormattedDateForSpecificTimezone(
          this.event.createdAt,
          this.timeZone
        );
      });
  }

  getDeviceNameFeature(
    deviceId: string
  ): Observable<Feature<DeviceNameProperties, DeviceNameCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature<
      DeviceNameProperties,
      DeviceNameCommands
    >(FeatureNames.deviceName, deviceId);
  }

  getRoom(featureName: string): Observable<Feature<RoomProperties, RoomCommands>> {
    if (featureName === FeatureNames.rooms) {
      return of(null);
    }

    const featureNameRoomId = featureName.split('.').slice(0, 2).join('.');

    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      featureNameRoomId,
      DeviceIds.RoomControl
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
